import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Bell, Mail, Sparkles, Loader2 } from 'lucide-react';
import { api, type AgentRuleShape, type AgentTriggerType } from '@/lib/api';
import { useCoursesQuery } from '@/hooks/queries/useCourses';
import { cn } from '@/lib/utils';

/**
 * Settings tab for managing EduBorg Agent rules.
 *
 * Lists this instructor's rules + recent firings + a small "Add rule" form.
 * Cron-driven rules (assignment_due_24h, student_inactive_7d, low_score)
 * are matched once per hour; new_discussion_post fires inline. We surface
 * both in the same list — the lastFiredAt column tells the story.
 */
const TRIGGER_OPTIONS: Array<{ key: AgentTriggerType; label: string; hint: string }> = [
  {
    key: 'assignment_due_24h',
    label: 'Assignment due in 24h',
    hint: 'Alert me before any of my assignments closes',
  },
  {
    key: 'student_inactive_7d',
    label: 'Student inactive 7+ days',
    hint: 'A student in my course who hasn’t submitted in a week',
  },
  {
    key: 'low_score',
    label: 'Low score (<70%)',
    hint: 'A new attempt scored below the passing threshold',
  },
  {
    key: 'new_discussion_post',
    label: 'New discussion post',
    hint: 'Anyone posts in a thread in one of my courses',
  },
];

const ACTION_OPTIONS = [
  { key: 'notify', label: 'Bell only' },
  { key: 'email', label: 'Email only' },
  { key: 'both', label: 'Bell + email' },
] as const;

export function AgentRulesTab({ token }: { token: string }) {
  const qc = useQueryClient();
  const rulesQuery = useQuery({
    queryKey: ['agent', 'rules'],
    queryFn: () => api.agent.listRules(token),
    staleTime: 30_000,
  });
  const logsQuery = useQuery({
    queryKey: ['agent', 'logs'],
    queryFn: () => api.agent.listLogs(token),
    staleTime: 30_000,
  });
  const coursesQuery = useCoursesQuery(token);
  const courses = coursesQuery.data?.courses ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.agent.deleteRule(token, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent', 'rules'] });
    },
  });

  const [composing, setComposing] = useState(false);

  return (
    <div className="space-y-6 py-6">
      <section>
        <header className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold">Your rules</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              EduBorg watches your courses and pings you when something matches.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" /> Add rule
          </button>
        </header>

        {rulesQuery.isLoading ? (
          <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Loading rules…
          </div>
        ) : (rulesQuery.data?.rules ?? []).length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
            No rules yet. Add one to start letting EduBorg watch for you.
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
            {rulesQuery.data?.rules.map((r) => (
              <RuleRow
                key={r.id}
                rule={r}
                courseTitle={courses.find((c) => c.id === r.courseId)?.title ?? null}
                onDelete={() => void deleteMutation.mutateAsync(r.id)}
                isDeleting={deleteMutation.isPending}
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-2 font-display text-lg font-semibold">Recent firings</h2>
        {logsQuery.isLoading ? (
          <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Loading…
          </div>
        ) : (logsQuery.data?.logs ?? []).length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
            Nothing fired yet — when a rule matches, you'll see it here and in the bell icon.
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
            {logsQuery.data?.logs.slice(0, 30).map((l) => {
              const payload = l.payload as { title?: string; body?: string } | null;
              return (
                <li key={l.id} className="p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{payload?.title ?? l.triggerType}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {new Date(l.firedAt).toLocaleString()}
                    </span>
                  </div>
                  {payload?.body && (
                    <div className="mt-0.5 text-xs text-muted-foreground">{payload.body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {composing && (
        <NewRuleDialog
          token={token}
          courses={courses}
          onClose={() => setComposing(false)}
          onCreated={() => {
            setComposing(false);
            void qc.invalidateQueries({ queryKey: ['agent', 'rules'] });
          }}
        />
      )}
    </div>
  );
}

function RuleRow({
  rule,
  courseTitle,
  onDelete,
  isDeleting,
}: {
  rule: AgentRuleShape;
  courseTitle: string | null;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const trigger = TRIGGER_OPTIONS.find((t) => t.key === rule.triggerType);
  return (
    <li className="flex items-center justify-between gap-3 p-3 text-sm">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
        <div>
          <div className="font-medium">{trigger?.label ?? rule.triggerType}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              {rule.actionType === 'email' ? (
                <Mail className="h-3 w-3" />
              ) : (
                <Bell className="h-3 w-3" />
              )}
              {ACTION_OPTIONS.find((a) => a.key === rule.actionType)?.label ?? rule.actionType}
            </span>
            <span>·</span>
            <span>{courseTitle ? `course: ${courseTitle}` : 'all my courses'}</span>
            {rule.lastFiredAt && (
              <>
                <span>·</span>
                <span>last fired {new Date(rule.lastFiredAt).toLocaleString()}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        aria-label="Delete rule"
        className={cn(
          'rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50',
        )}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function NewRuleDialog({
  token,
  courses,
  onClose,
  onCreated,
}: {
  token: string;
  courses: Array<{ id: string; title: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [triggerType, setTriggerType] = useState<AgentTriggerType>('assignment_due_24h');
  const [actionType, setActionType] = useState<'notify' | 'email' | 'both'>('notify');
  const [courseId, setCourseId] = useState<string>(''); // '' === all courses

  const createMutation = useMutation({
    mutationFn: () =>
      api.agent.createRule(token, {
        triggerType,
        actionType,
        courseId: courseId || null,
      }),
    onSuccess: onCreated,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <form
        className="w-full max-w-md rounded-2xl border border-border bg-background shadow-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          if (createMutation.isPending) return;
          void createMutation.mutateAsync();
        }}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="font-display text-lg font-semibold">New Agent rule</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </header>

        <div className="space-y-4 p-5">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Trigger</span>
            <select
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value as AgentTriggerType)}
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {TRIGGER_OPTIONS.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-muted-foreground">
              {TRIGGER_OPTIONS.find((t) => t.key === triggerType)?.hint}
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium">When it fires</span>
            <div className="flex flex-wrap gap-2">
              {ACTION_OPTIONS.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setActionType(a.key)}
                  className={cn(
                    'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                    actionType === a.key
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-muted-foreground hover:bg-secondary',
                  )}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium">Scope</span>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All my courses</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  Course: {c.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-5 py-3">
          {createMutation.isError && (
            <span className="mr-auto text-xs text-destructive">Couldn't create — try again.</span>
          )}
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Create rule
          </button>
        </footer>
      </form>
    </div>
  );
}
